import type { ReactNode, SyntheticEvent } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Container, Divider, Tab, Tabs } from '@mui/material';

import {
  JOTBILL_HEADER_HEIGHT_FALLBACK_PX,
  JOTBILL_HEADER_HEIGHT_VAR,
  jotBillHeaderHeightPx,
} from './jotBillHeaderHeight';

// The shell owns the page width: header and every tab panel share the same
// Container size; tab content must fill 100% and never set its own
// page-level max-width.
const JOTBILL_CONTAINER_MAX_WIDTH = 'xxl' as const;

export type JotBillCenterTab<TabId extends string = string> = {
  id: TabId;
  label: ReactNode;
  subtitle: ReactNode;
  content: ReactNode;
  badgeCount?: number;
  disabled?: boolean;
  visible?: boolean;
};

type JotBillCenterShellProps<TabId extends string> = {
  tabs: readonly JotBillCenterTab<TabId>[];
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  headerActions?: ReactNode;
  /**
   * Tabs that stay VISIBLE but are inert — for participant-facing previews
   * where non-target product areas remain discoverable without being
   * navigable. A locked tab follows the WAI-ARIA disabled-tab pattern:
   * focusable (arrow keys can reach it so assistive tech can discover it)
   * with `aria-disabled="true"`, a quiet title tooltip, and activation
   * ignored — clicking or pressing Enter never calls `onTabChange`.
   * Undefined (the default, and production's behavior) locks nothing.
   */
  lockedTabIds?: readonly TabId[];
};

const formatBadgeCount = (count: number): string => (count > 99 ? '99+' : String(count));

export const JotBillCenterShell = <TabId extends string>({
  tabs,
  activeTab,
  onTabChange,
  headerActions,
  lockedTabIds,
}: JotBillCenterShellProps<TabId>): React.ReactElement | null => {
  const visibleTabs = useMemo(() => tabs.filter(tab => tab.visible !== false), [tabs]);
  const lockedTabIdSet = useMemo(() => new Set<TabId>(lockedTabIds ?? []), [lockedTabIds]);

  /* Observe the sticky header so nested sticky content tracks its height. */
  const [headerEl, setHeaderEl] = useState<HTMLElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState<number | null>(null);
  useEffect(() => {
    if (!headerEl || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(entries => {
      /* Border-box first: the var must cover the header's FULL footprint,
         borders included, if one is ever added. contentRect is the fallback
         for engines without borderBoxSize. */
      const borderBox = entries[0]?.borderBoxSize?.[0]?.blockSize;
      const measured = typeof borderBox === 'number' ? borderBox : entries[0]?.contentRect.height;
      if (typeof measured === 'number') setHeaderHeight(jotBillHeaderHeightPx(measured));
    });
    observer.observe(headerEl);
    return () => observer.disconnect();
  }, [headerEl]);
  const activeRegistration =
    visibleTabs.find(tab => tab.id === activeTab && !tab.disabled && !lockedTabIdSet.has(tab.id)) ??
    visibleTabs.find(tab => !tab.disabled && !lockedTabIdSet.has(tab.id));

  if (!activeRegistration) {
    return null;
  }

  const handleTabChange = (_event: SyntheticEvent, nextTab: TabId): void => {
    if (
      !visibleTabs.some(tab => tab.id === nextTab && !tab.disabled) ||
      lockedTabIdSet.has(nextTab)
    )
      return;
    onTabChange(nextTab);
  };

  return (
    <Box
      /* Deliberate `style` (not `sx`) — the DECLARED exception to the
         no-inline-style rule: this value is a live ResizeObserver
         measurement, and `sx` mints a new emotion class per distinct value,
         which would churn the stylesheet on every tick of a window resize.
         An inline custom property is the standard mechanism for
         measurement-driven CSS variables. Static styling stays in `sx`. */
      style={
        headerHeight != null
          ? ({ [JOTBILL_HEADER_HEIGHT_VAR]: `${headerHeight}px` } as React.CSSProperties)
          : undefined
      }
      sx={{ position: 'relative' }}
    >
      <Box
        ref={setHeaderEl}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1099,
          bgcolor: 'background.paper',
        }}
      >
        <Container maxWidth={JOTBILL_CONTAINER_MAX_WIDTH}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pt: 1 }}>
            <Tabs
              variant="scrollable"
              scrollButtons="auto"
              value={activeRegistration.id}
              onChange={handleTabChange}
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 40,
                '& .MuiTab-root': {
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  minHeight: 40,
                  py: 1,
                  color: 'text.secondary',
                  '&.Mui-selected': {
                    fontWeight: 600,
                    color: 'primary.main',
                  },
                },
                '& .MuiTabs-indicator': {
                  height: 2,
                  borderRadius: '2px 2px 0 0',
                },
              }}
            >
              {visibleTabs.map(tab => {
                const badgeCount = tab.badgeCount ?? 0;
                const locked = lockedTabIdSet.has(tab.id);

                return (
                  <Tab
                    key={tab.id}
                    value={tab.id}
                    disabled={tab.disabled}
                    aria-disabled={locked ? 'true' : undefined}
                    title={locked ? 'Not part of this preview' : undefined}
                    disableRipple={locked}
                    sx={locked ? { opacity: 0.45, '&:hover': { opacity: 0.45 } } : undefined}
                    id={`jotbill-tab-${tab.id}`}
                    aria-controls={`jotbill-tabpanel-${tab.id}`}
                    label={
                      badgeCount > 0 ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          {tab.label}
                          <Box
                            component="span"
                            sx={{
                              bgcolor: 'error.main',
                              color: '#fff',
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              height: 18,
                              minWidth: 18,
                              borderRadius: '9px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              px: 0.5,
                              lineHeight: 1,
                            }}
                          >
                            {formatBadgeCount(badgeCount)}
                          </Box>
                        </Box>
                      ) : (
                        tab.label
                      )
                    }
                  />
                );
              })}
            </Tabs>
            {headerActions && <Box sx={{ flexShrink: 0 }}>{headerActions}</Box>}
          </Box>
        </Container>
        <Divider />
      </Box>

      <Box
        sx={{
          bgcolor: 'grey.50',
          /* Same measured height, replacing the old magic 160px. */
          minHeight: `calc(100vh - var(${JOTBILL_HEADER_HEIGHT_VAR}, ${JOTBILL_HEADER_HEIGHT_FALLBACK_PX}px))`,
        }}
      >
        <Container
          id={`jotbill-tabpanel-${activeRegistration.id}`}
          role="tabpanel"
          aria-labelledby={`jotbill-tab-${activeRegistration.id}`}
          sx={{ py: 3 }}
          maxWidth={JOTBILL_CONTAINER_MAX_WIDTH}
        >
          {activeRegistration.content}
        </Container>
      </Box>
    </Box>
  );
};
